import { useState } from "react";
import { processLeaderWithdrawal } from "../../api/payment";
import { FaSpinner, FaMoneyBillWave } from "react-icons/fa";
import EmailSelector from "../shared/EmailSelector";
import {
  showWarningAlert,
  showErrorAlert,
  showConfirmationAlert,
  showSuccessAlert,
} from "./withdrawalAlerts";

const DeductionForm = () => {
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [amount, setAmount] = useState("");
  const [processingWithdrawal, setProcessingWithdrawal] = useState(false);

  const handleLeaderChange = (leader) => {
    setSelectedLeader(leader);
    setAmount("");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setAmount(value);
  };

  const handleSetMaxAmount = () => {
    if (selectedLeader && selectedLeader.earnings !== undefined) {
      setAmount(Math.floor(selectedLeader.earnings).toString());
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleWithdrawal = async () => {
    if (!selectedLeader || !amount) {
      showWarningAlert(
        "Por favor seleccione un propietario e ingrese un monto"
      );
      return;
    }

    const withdrawalAmount = parseFloat(amount);

    if (isNaN(withdrawalAmount) || withdrawalAmount <= 0) {
      showErrorAlert(
        "Monto inválido",
        "Por favor ingrese un monto válido mayor a 0"
      );
      return;
    }

    if (withdrawalAmount > selectedLeader.earnings) {
      showErrorAlert(
        "Monto excede el balance",
        `No se puede descontar más del balance disponible (${formatCurrency(
          selectedLeader.earnings
        )})`
      );
      return;
    }

    const confirmResult = await showConfirmationAlert(
      selectedLeader,
      withdrawalAmount,
      formatCurrency
    );

    if (!confirmResult.isConfirmed) {
      return;
    }

    setProcessingWithdrawal(true);

    try {
      await processLeaderWithdrawal(selectedLeader.email, withdrawalAmount);

      const newBalance = selectedLeader.earnings - withdrawalAmount;

      showSuccessAlert(withdrawalAmount, newBalance, formatCurrency);

      setSelectedLeader({
        ...selectedLeader,
        earnings: newBalance,
      });
      setAmount("");
    } catch (error) {
      console.error("Error al procesar retiro:", error);

      showErrorAlert(
        "Error al procesar retiro",
        error?.message ||
          "No se pudo procesar el retiro. Inténtelo de nuevo más tarde."
      );
    } finally {
      setProcessingWithdrawal(false);
    }
  };

  return (
    <div className="deduction-card">
      <div className="deduction-card__header">
        <div className="deduction-card__icon">
          <FaMoneyBillWave />
        </div>
        <div className="deduction-card__title">
          <h2>Descontar Monto a Propietario</h2>
          <p>
            Procese descuentos de balance para propietarios que solicitaron
            retiros
          </p>
        </div>
      </div>
      <div className="deduction-card__body">
        <div className="deduction-form">
          <EmailSelector
            value={selectedLeader}
            onChange={handleLeaderChange}
            error=""
            label="Seleccionar Propietario (Correo Electrónico)"
            placeholder="Buscar por correo electrónico"
          />

          {selectedLeader && (
            <div className="selected-leader-info">
              <div className="info-row">
                <span className="info-label">Propietario:</span>
                <span className="info-value">
                  {`${selectedLeader.first_name || ""} ${
                    selectedLeader.last_name || ""
                  }`.trim()}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{selectedLeader.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Balance Disponible:</span>
                <span className="info-value info-value--highlight">
                  {formatCurrency(selectedLeader.earnings || 0)}
                </span>
              </div>
            </div>
          )}

          <div className="input-group">
            <label className="input-group__label">
              Monto a Descontar (COP){" "}
              <span className="input-group__required">*</span>
            </label>
            <div className="input-with-button">
              <input
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="Ingrese el monto"
                className="input-group__input"
              />
              {selectedLeader && (
                <button
                  type="button"
                  className="btn-max"
                  onClick={handleSetMaxAmount}
                >
                  Monto Máximo
                </button>
              )}
            </div>
          </div>

          {selectedLeader && (
            <div className="form-actions">
              <button
                type="button"
                className="btn-deduct"
                onClick={handleWithdrawal}
                disabled={processingWithdrawal}
              >
                {processingWithdrawal ? (
                  <>
                    <FaSpinner className="spinner" /> Procesando...
                  </>
                ) : (
                  <>
                    <FaMoneyBillWave /> Descontar Monto
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeductionForm;
